import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, AlertCircle, ChevronDown, Users, Tag } from 'lucide-react';
import AppLayout from '../../src/components/AppLayout';
import VenueChatbot from '../../src/components/VenueChatbot';
import '../styles/venue-browse.css';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';

const resolveImageSrc = (value) => {
  return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
};

const TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'semi-indoor', label: 'Semi-Indoor' },
  { value: 'other', label: 'Other' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'price-low', label: 'Price (Low to High)' },
  { value: 'price-high', label: 'Price (High to Low)' },
  { value: 'capacity', label: 'Capacity (High to Low)' },
];

const PRICE_START = 250;
const PRICE_STEP = 500;
const PRICE_MAX = 20000;

const PRICE_OPTIONS = Array.from(
  { length: Math.floor((PRICE_MAX - PRICE_START) / PRICE_STEP) + 1 },
  (_, index) => {
    const value = String(PRICE_START + (index * PRICE_STEP));
    return {
      value,
      label: `P${Number(value).toLocaleString('en-PH')}`,
    };
  }
);

const ANY_PRICE_OPTION = { value: '', label: 'Any' };
const MIN_PRICE_OPTIONS = [ANY_PRICE_OPTION, ...PRICE_OPTIONS];

function FilterDropdown({ id, label, value, options, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className={`filter-dropdown ${isOpen ? 'filter-dropdown--open' : ''}`} ref={rootRef}>
      <button
        id={id}
        type="button"
        className="filter-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{label}: {selectedOption.label}</span>
        <ChevronDown size={15} className="filter-dropdown__icon" aria-hidden="true" />
      </button>

      {isOpen && (
        <ul className="filter-dropdown__menu" role="listbox" aria-labelledby={id}>
          {options.map((option) => (
            <li key={option.value} role="option" aria-selected={option.value === value}>
              <button
                type="button"
                className={`filter-dropdown__option ${option.value === value ? 'filter-dropdown__option--active' : ''}`}
                onClick={() => handleSelect(option.value)}
              >
                {label}: {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

FilterDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function VenueBrowsePage({ user, onLogout }) {
  const navigate = useNavigate();

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete';

  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    sortBy: 'name',
    minPrice: '',
    maxPrice: '',
  });
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileDraftFilters, setMobileDraftFilters] = useState({
    type: 'all',
    sortBy: 'name',
    minPrice: '',
    maxPrice: '',
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const syncViewport = () => {
      const mobile = mediaQuery.matches;
      setIsMobileView(mobile);
      if (!mobile) {
        setMobileFiltersOpen(false);
      }
    };

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    setMobileDraftFilters(filters);
  }, [filters]);

  const activeFilterSource = isMobileView ? mobileDraftFilters : filters;

  const filteredMaxPriceOptions = activeFilterSource.minPrice === ''
    ? [ANY_PRICE_OPTION, ...PRICE_OPTIONS]
    : [
      ANY_PRICE_OPTION,
      ...PRICE_OPTIONS.filter((option) => Number(option.value) >= Number(activeFilterSource.minPrice)),
    ];

  useEffect(() => {
    if (filters.minPrice !== '' && filters.maxPrice !== '' && Number(filters.maxPrice) < Number(filters.minPrice)) {
      setFilters((prev) => ({ ...prev, maxPrice: prev.minPrice }));
    }
  }, [filters.minPrice, filters.maxPrice]);

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ pageSize: '50' });
      if (filters.type !== 'all') {
        params.append('type', filters.type);
      }

      const response = await fetch(`${API_BASE_URL}/api/venues?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to load venues');
      }

      const data = await response.json();
      let sortedVenues = data.results || [];

      const hasMinPrice = filters.minPrice !== '' && !Number.isNaN(Number(filters.minPrice));
      const hasMaxPrice = filters.maxPrice !== '' && !Number.isNaN(Number(filters.maxPrice));

      if (hasMinPrice || hasMaxPrice) {
        const min = hasMinPrice ? Number(filters.minPrice) : 0;
        const max = hasMaxPrice ? Number(filters.maxPrice) : Number.POSITIVE_INFINITY;
        sortedVenues = sortedVenues.filter((venue) => {
          const basePrice = Number(venue.price6hrs || venue.ratePerHour || 0);
          const discount = Number(venue.discountPercentage || 0);
          const effectivePrice = basePrice - (basePrice * discount / 100);
          return effectivePrice >= min && effectivePrice <= max;
        });
      }

      if (filters.sortBy === 'name') {
        sortedVenues.sort((a, b) => a.name.localeCompare(b.name));
      } else if (filters.sortBy === 'price-low') {
        sortedVenues.sort((a, b) => {
          const pa = Number(a.price6hrs || a.ratePerHour || 0);
          const pb = Number(b.price6hrs || b.ratePerHour || 0);
          const da = Number(a.discountPercentage || 0);
          const db = Number(b.discountPercentage || 0);
          return (pa - pa * da / 100) - (pb - pb * db / 100);
        });
      } else if (filters.sortBy === 'price-high') {
        sortedVenues.sort((a, b) => {
          const pa = Number(a.price6hrs || a.ratePerHour || 0);
          const pb = Number(b.price6hrs || b.ratePerHour || 0);
          const da = Number(a.discountPercentage || 0);
          const db = Number(b.discountPercentage || 0);
          return (pb - pb * db / 100) - (pa - pa * da / 100);
        });
      } else if (filters.sortBy === 'capacity') {
        sortedVenues.sort((a, b) => (b.capacity || 0) - (a.capacity || 0));
      }

      setVenues(sortedVenues);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('Unable to load venues. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleFilterChange = (field) => (eventOrValue) => {
    const nextValue = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
    setFilters((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleMobileFilterChange = (field) => (eventOrValue) => {
    const nextValue = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;

    setMobileDraftFilters((prev) => {
      const updated = { ...prev, [field]: nextValue };

      if (field === 'minPrice' && updated.minPrice !== '' && updated.maxPrice !== '' && Number(updated.maxPrice) < Number(updated.minPrice)) {
        updated.maxPrice = updated.minPrice;
      }

      if (field === 'maxPrice' && updated.minPrice !== '' && updated.maxPrice !== '' && Number(updated.maxPrice) < Number(updated.minPrice)) {
        updated.maxPrice = updated.minPrice;
      }

      return updated;
    });
  };

  const handleApplyMobileFilters = () => {
    setFilters(mobileDraftFilters);
    setMobileFiltersOpen(false);
  };

  const handleVenueClick = (venueId) => {
    navigate(`/venues/${venueId}`);
  };

  if (!isVerified) {
    return (
      <AppLayout title="Venue Booking" user={user} onLogout={onLogout}>
        <div className="venue-browse-page">
          <div className="rejection-lockout" style={{
            maxWidth: '600px',
            margin: '64px auto',
            background: 'var(--color-surface, #ffffff)',
            padding: '48px',
            borderRadius: '24px',
            textAlign: 'center',
            boxShadow: 'var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06))',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: '#FFFBEB',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 24px',
              color: '#F59E0B'
            }}>
              <AlertCircle size={36} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: '#111827' }}>Verification Required</h1>
            <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '32px', lineHeight: '1.6' }}>
              {isIncomplete
                ? "Your membership profile is incomplete. Please verify your details to access venue booking."
                : "Your membership application is still under review. Once approved, you will gain access to venue booking."}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {isIncomplete ? (
                <button
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  View Application
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '12px 24px',
                  borderRadius: '999px',
                  border: '1px solid #d1d5db',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );

  }

  return (
    <AppLayout title="Venue Booking" user={user} onLogout={onLogout}>
      <div className="venue-browse-page">
        <div className="venue-browse-page__toolbar">
          {isMobileView ? (
            <div className="venue-browse-page__mobile-filters" role="group" aria-label="Venue filters">
              <button
                type="button"
                className="venue-browse-page__mobile-filter-trigger"
                onClick={() => setMobileFiltersOpen((prev) => !prev)}
                aria-expanded={mobileFiltersOpen}
                aria-controls="venue-mobile-filter-panel"
              >
                <span>Filters</span>
                <ChevronDown size={16} className={`venue-browse-page__mobile-filter-icon${mobileFiltersOpen ? ' venue-browse-page__mobile-filter-icon--open' : ''}`} />
              </button>

              {mobileFiltersOpen && (
                <div id="venue-mobile-filter-panel" className="venue-browse-page__mobile-filter-panel">
                  <div className="venue-browse-page__filter-group">
                    <FilterDropdown
                      id="type-filter-mobile"
                      label="Venue Type"
                      value={mobileDraftFilters.type}
                      onChange={handleMobileFilterChange('type')}
                      options={TYPE_OPTIONS}
                    />
                  </div>

                  <div className="venue-browse-page__filter-group">
                    <FilterDropdown
                      id="sort-filter-mobile"
                      label="Sort By"
                      value={mobileDraftFilters.sortBy}
                      onChange={handleMobileFilterChange('sortBy')}
                      options={SORT_OPTIONS}
                    />
                  </div>

                  <div className="venue-browse-page__filter-group venue-browse-page__filter-group--price" aria-label="Filter by price range">
                    <div className="venue-browse-page__price-inputs">
                      <FilterDropdown
                        id="min-price-filter-mobile"
                        label="Min Price"
                        value={mobileDraftFilters.minPrice}
                        onChange={handleMobileFilterChange('minPrice')}
                        options={MIN_PRICE_OPTIONS}
                      />
                      <span className="venue-browse-page__price-separator" aria-hidden="true">-</span>
                      <FilterDropdown
                        id="max-price-filter-mobile"
                        label="Max Price"
                        value={mobileDraftFilters.maxPrice}
                        onChange={handleMobileFilterChange('maxPrice')}
                        options={filteredMaxPriceOptions}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="venue-browse-page__mobile-filter-apply"
                    onClick={handleApplyMobileFilters}
                  >
                    Show
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="venue-browse-page__filters" role="group" aria-label="Venue filters">
              <div className="venue-browse-page__filter-group">
                <FilterDropdown
                  id="type-filter"
                  label="Venue Type"
                  value={filters.type}
                  onChange={handleFilterChange('type')}
                  options={TYPE_OPTIONS}
                />
              </div>

              <div className="venue-browse-page__filter-group">
                <FilterDropdown
                  id="sort-filter"
                  label="Sort By"
                  value={filters.sortBy}
                  onChange={handleFilterChange('sortBy')}
                  options={SORT_OPTIONS}
                />
              </div>

              <div className="venue-browse-page__filter-group venue-browse-page__filter-group--price" aria-label="Filter by price range">
                <div className="venue-browse-page__price-inputs">
                  <FilterDropdown
                    id="min-price-filter"
                    label="Min Price"
                    value={filters.minPrice}
                    onChange={handleFilterChange('minPrice')}
                    options={MIN_PRICE_OPTIONS}
                  />
                  <span className="venue-browse-page__price-separator" aria-hidden="true">-</span>
                  <FilterDropdown
                    id="max-price-filter"
                    label="Max Price"
                    value={filters.maxPrice}
                    onChange={handleFilterChange('maxPrice')}
                    options={filteredMaxPriceOptions}
                  />
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="venue-browse-page__action-button"
            onClick={() => navigate('/my-venue-bookings')}
          >
            View My Bookings
          </button>
        </div>

        {loading && (
          <div className="venue-browse-page__loading">
            <p>Loading venues...</p>
          </div>
        )}

        {error && (
          <div className="venue-browse-page__error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && venues.length === 0 && (
          <div className="venue-browse-page__empty">
            <Building2 size={48} />
            <p>No venues available at the moment.</p>
          </div>
        )}

        {!loading && !error && venues.length > 0 && (
          <div className="venue-browse-page__grid">
            {venues.map((venue) => (
              <div
                key={venue.id}
                className="venue-card"
                onClick={() => handleVenueClick(venue.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleVenueClick(venue.id);
                  }
                }}
              >
                <div className="venue-card__image">
                  {venue.featuredImage ? (
                    <img src={resolveImageSrc(venue.featuredImage)} alt={venue.name} />
                  ) : (
                    <div className="venue-card__image-placeholder">
                      <Building2 size={40} />
                    </div>
                  )}
                </div>

                <div className="venue-card__content">
                  <h3 className="venue-card__title">{venue.name}</h3>

                  <div className="venue-card__location" title={venue.location || 'Location unavailable'}>
                    <MapPin size={14} />
                    <span>{venue.location || 'Location unavailable'}</span>
                  </div>

                  <p className="venue-card__description">
                    {venue.shortDescription || venue.description || ''}
                  </p>

                  <div className="venue-card__capacity">
                    <Users size={14} />
                    <span>Up to {venue.maxPax || venue.capacity || '—'} people</span>
                  </div>

                  <div className="venue-card__pricing">
                    {Number(venue.discountPercentage || 0) > 0 && (
                      <span className="venue-card__discount-badge">
                        <Tag size={12} />
                        {venue.discountPercentage}% OFF
                      </span>
                    )}
                    {(() => {
                      const price6 = Number(venue.price6hrs || 0);
                      const discount = Number(venue.discountPercentage || 0);
                      const discounted6 = price6 - (price6 * discount / 100);
                      return (
                        <div className="venue-card__price-packages">
                          <div className="venue-card__price-row">
                            <span className="venue-card__price-label">6 hrs</span>
                            {discount > 0 ? (
                              <>
                                <span className="venue-card__price-original">₱{price6.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                                <span className="venue-card__price-discounted">₱{discounted6.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                              </>
                            ) : (
                              <span className="venue-card__price-value">₱{price6.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                            )}
                          </div>
                          <div className="venue-card__price-row">
                            <span className="venue-card__price-label">12 hrs</span>
                            {(() => {
                              const p12 = Number(venue.price12hrs || 0);
                              const d12 = p12 - (p12 * discount / 100);
                              return discount > 0 ? (
                                <>
                                  <span className="venue-card__price-original">₱{p12.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                                  <span className="venue-card__price-discounted">₱{d12.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                                </>
                              ) : (
                                <span className="venue-card__price-value">₱{p12.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                              );
                            })()}
                          </div>
                          <div className="venue-card__price-row">
                            <span className="venue-card__price-label">24 hrs</span>
                            {(() => {
                              const p24 = Number(venue.price24hrs || 0);
                              const d24 = p24 - (p24 * discount / 100);
                              return discount > 0 ? (
                                <>
                                  <span className="venue-card__price-original">₱{p24.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                                  <span className="venue-card__price-discounted">₱{d24.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                                </>
                              ) : (
                                <span className="venue-card__price-value">₱{p24.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <button type="button" className="venue-card__button">
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <VenueChatbot user={user} onBookingCreated={fetchVenues} />
    </AppLayout>
  );
}

VenueBrowsePage.propTypes = {
  user: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
};
